/**
 * Soft OTA prompt for non-critical browsing.
 *
 * Auth surfaces and resume-from-background apply silently (see
 * OtaUpdateController). This banner is only for moments where a reload would
 * be surprising mid-scroll (explore, trips, profile) — one clear action, no
 * modal sheet, no blocking the product.
 *
 * Hidden on critical routes (check-in, checkout, listing, KYC) so we never
 * invite a reload that throws away photos or form state.
 */
import { View, Text } from 'react-native'
import { useTranslation } from 'react-i18next'
import { usePathname } from 'expo-router'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { Pressable } from '@/components/ui/Pressable'
import { COLORS, FONTS, RADIUS, SPACING } from '@/lib/brand'
import { Z_INDEX } from '@/lib/theme/tokens'
import { isOtaCriticalRoute } from '@/lib/updates/ota-policy'

interface UpdateReadyBannerProps {
  visible: boolean
  onApply: () => void
}

export function UpdateReadyBanner({ visible, onApply }: UpdateReadyBannerProps) {
  const { t } = useTranslation()
  const pathname = usePathname()
  const insets = useSafeAreaInsets()

  if (!visible) return null
  // Defense in depth — controller already filters; keep the component safe alone.
  if (isOtaCriticalRoute(pathname)) return null

  return (
    <View
      testID="update-ready.banner"
      pointerEvents="box-none"
      style={{
        position: 'absolute',
        left: SPACING.md,
        right: SPACING.md,
        bottom: insets.bottom + SPACING.xl,
        zIndex: Z_INDEX.toast,
      }}
    >
      <Pressable
        onPress={onApply}
        testID="update-ready.apply-button"
        accessibilityRole="button"
        accessibilityLabel={t('updates.ready_a11y')}
        style={({ pressed }) => ({
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: SPACING.md,
          minHeight: 52,
          paddingVertical: SPACING.sm + SPACING.xs,
          paddingHorizontal: SPACING.md,
          borderRadius: RADIUS.lg,
          // Coral de marca: novedad, no error.
          backgroundColor: COLORS.coral.dark,
          opacity: pressed ? 0.88 : 1,
        })}
      >
        <Text
          numberOfLines={2}
          style={{
            flex: 1,
            minWidth: 0,
            fontFamily: FONTS.body,
            fontSize: 14,
            lineHeight: 19,
            color: '#FFFFFF',
          }}
        >
          {t('updates.ready_title')}
        </Text>
        <Text
          numberOfLines={1}
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 14,
            lineHeight: 19,
            color: '#FFFFFF',
          }}
        >
          {t('updates.ready_action')}
        </Text>
      </Pressable>
    </View>
  )
}
